/* global google */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from '@vis.gl/react-google-maps/dist/index.umd';

const eventMap = {
  onCenterChanged: 'center_changed',
  onRadiusChanged: 'radius_changed',
  onClick: 'click',
  onDblClick: 'dblclick',
  onDrag: 'drag',
  onDragEnd: 'dragend',
  onDragStart: 'dragstart',
  onMouseDown: 'mousedown',
  onMouseMove: 'mousemove',
  onMouseOut: 'mouseout',
  onMouseOver: 'mouseover',
  onMouseUp: 'mouseup',
  onRightClick: 'rightclick',
};

const ensureLatLng = (value) => {
  if (!value) return null;
  return value instanceof google.maps.LatLng
    ? value
    : new google.maps.LatLng(value.lat, value.lng);
};

const Circle = ({
  options,
  center,
  radius,
  draggable,
  editable,
  visible,
  onDblClick,
  onDragEnd,
  onDragStart,
  onMouseDown,
  onMouseMove,
  onMouseOut,
  onMouseOver,
  onMouseUp,
  onRightClick,
  onClick,
  onDrag,
  onCenterChanged,
  onRadiusChanged,
  onLoad,
  onUnmount,
}) => {
  const map = useMap();
  const [circle, setCircle] = useState(null);
  const listenersRef = useRef([]);

  const eventHandlers = useMemo(
    () => ({
      onCenterChanged,
      onRadiusChanged,
      onClick,
      onDblClick,
      onDrag,
      onDragEnd,
      onDragStart,
      onMouseDown,
      onMouseMove,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onRightClick,
    }),
    [
      onCenterChanged,
      onRadiusChanged,
      onClick,
      onDblClick,
      onDrag,
      onDragEnd,
      onDragStart,
      onMouseDown,
      onMouseMove,
      onMouseOut,
      onMouseOver,
      onMouseUp,
      onRightClick,
    ]
  );

  useEffect(() => {
    if (!map) return undefined;

    const circleInstance = new google.maps.Circle({
      ...(options || {}),
      map,
    });

    if (typeof radius === 'number') {
      circleInstance.setRadius(radius);
    }

    const latLngCenter = ensureLatLng(center);
    if (latLngCenter) {
      circleInstance.setCenter(latLngCenter);
    }

    if (typeof visible !== 'undefined') {
      circleInstance.setVisible(visible);
    }

    if (typeof editable !== 'undefined') {
      circleInstance.setEditable(editable);
    }

    if (typeof draggable !== 'undefined') {
      circleInstance.setDraggable(draggable);
    }

    setCircle(circleInstance);
    if (onLoad) {
      onLoad(circleInstance);
    }

    return () => {
      listenersRef.current.forEach((listener) =>
        google.maps.event.removeListener(listener)
      );
      listenersRef.current = [];

      if (onUnmount) {
        onUnmount(circleInstance);
      }
      circleInstance.setMap(null);
      setCircle(null);
    };
  }, [map, center, draggable, editable, onLoad, onUnmount, options, radius, visible]);

  useEffect(() => {
    if (!circle) return;
    if (typeof radius === 'number') {
      circle.setRadius(radius);
    }
  }, [circle, radius]);

  useEffect(() => {
    if (!circle) return;
    const latLngCenter = ensureLatLng(center);
    if (latLngCenter) {
      circle.setCenter(latLngCenter);
    }
  }, [circle, center]);

  useEffect(() => {
    if (!circle) return;
    if (typeof draggable !== 'undefined') {
      circle.setDraggable(draggable);
    }
  }, [circle, draggable]);

  useEffect(() => {
    if (!circle) return;
    if (typeof editable !== 'undefined') {
      circle.setEditable(editable);
    }
  }, [circle, editable]);

  useEffect(() => {
    if (!circle) return;
    if (typeof visible !== 'undefined') {
      circle.setVisible(visible);
    }
  }, [circle, visible]);

  useEffect(() => {
    if (!circle || !options) return;
    circle.setOptions(options);
  }, [circle, options]);

  useEffect(() => {
    if (!circle) return;

    listenersRef.current.forEach((listener) =>
      google.maps.event.removeListener(listener)
    );
    listenersRef.current = [];

    Object.entries(eventMap).forEach(([propName, eventName]) => {
      const handler = eventHandlers[propName];
      if (handler) {
        listenersRef.current.push(
          google.maps.event.addListener(circle, eventName, handler)
        );
      }
    });
  }, [circle, eventHandlers]);

  return null;
};

export default Circle;

